import { kmeans } from 'ml-kmeans';

function rgbToHsv(r: number, g: number, b: number) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, v = max;
  const d = max - min;
  s = max === 0 ? 0 : d / max;
  if (max !== min) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s, v * 255];
}

export const extractColors = (imgElement: HTMLImageElement): string[] => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  
  // Scale down for performance, but keep it high enough for good sampling
  const MAX_SIZE = 150;
  const scale = Math.min(1, MAX_SIZE / Math.max(imgElement.width, imgElement.height));
  canvas.width = Math.max(1, Math.floor(imgElement.width * scale));
  canvas.height = Math.max(1, Math.floor(imgElement.height * scale));
  
  ctx.drawImage(imgElement, 0, 0, canvas.width, canvas.height);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  
  let pixels: number[][] = [];
  const fallbackPixels: number[][] = [];
  
  for (let i = 0; i < imageData.length; i += 4) {
    const r = imageData[i];
    const g = imageData[i + 1];
    const b = imageData[i + 2];
    const a = imageData[i + 3];
    
    // Ignore transparent pixels
    if (a < 128) continue;

    fallbackPixels.push([r, g, b]);

    const [h, s, v] = rgbToHsv(r, g, b);

    // Ignore colors with low saturation (e.g. < 15%)
    if (s < 0.15) continue;
    
    // Ignore very dark colors
    if (v < 20) continue;
    
    pixels.push([r, g, b]);
  }
  
  if (pixels.length === 0) {
    pixels = fallbackPixels;
  }
  
  if (pixels.length === 0) return [];

  // Run K-Means clustering with k=5 to better separate anti-aliased edges
  const k = Math.min(5, pixels.length);
  const ans = kmeans(pixels, k, { initialization: 'kmeans++' });
  
  // Count the number of pixels in each cluster
  const clusterCounts = new Array(k).fill(0);
  for (let i = 0; i < ans.clusters.length; i++) {
    clusterCounts[ans.clusters[i]]++;
  }
  
  // Combine centroids with their pixel counts
  let clustersWithCounts = ans.centroids.map((centroid, index) => ({
    centroid,
    count: clusterCounts[index]
  }));
  
  // Ignore colors covering < 2% of valid pixels
  const totalPixels = pixels.length;
  const filteredClusters = clustersWithCounts.filter(c => (c.count / totalPixels) >= 0.02);
  
  if (filteredClusters.length > 0) {
    clustersWithCounts = filteredClusters;
  }
  
  // Sort clusters by count in descending order
  clustersWithCounts.sort((a, b) => b.count - a.count);
  
  // Helper to convert RGB array to Hex
  const rgbToHex = (rgb: number[]) => {
    return '#' + rgb.map(v => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase();
  };
  
  // Distance metric to find visually distinct colors
  const distance = (c1: number[], c2: number[]) => {
    return Math.sqrt(
      Math.pow(c1[0] - c2[0], 2) +
      Math.pow(c1[1] - c2[1], 2) +
      Math.pow(c1[2] - c2[2], 2)
    );
  };
  
  const primaryCentroid = clustersWithCounts[0].centroid;
  const primaryHex = rgbToHex(primaryCentroid);
  
  if (clustersWithCounts.length === 1) {
    return [primaryHex, primaryHex];
  }
  
  // Find the second largest cluster that is visually distinct from the primary
  const secondaryCluster = clustersWithCounts.slice(1).find(c => distance(primaryCentroid, c.centroid) > 60);
  
  const secondaryCentroid = secondaryCluster ? secondaryCluster.centroid : clustersWithCounts[1].centroid;
  const secondaryHex = rgbToHex(secondaryCentroid);
  
  return [primaryHex, secondaryHex];
};
